'use client';

import Link from 'next/link';
import { MapPin, Star, ArrowRight } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { useLocalization } from '@/core/hooks/useLocalization';
import { useTranslation } from '@/core/i18n';
import type { FeaturedHotelCard } from '../types/hotels.types';
import { usePublicHotels } from '../hooks/useHotels';
import { SkeletonCard } from '@/shared/feedback';

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                             */
/* -------------------------------------------------------------------------- */

function StarRating({ count }: { count: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: count }).map((_, i) => (
        <Star key={i} size={13} className="fill-hms-gold text-hms-gold" />
      ))}
    </div>
  );
}

function HotelCardItem({ hotel, index }: { hotel: FeaturedHotelCard; index: number }) {
  const { formatMoney, currencySymbol } = useLocalization();
  const { t } = useTranslation();

  return (
    <Link
      id={`hotels-featured-card-${index}`}
      href={`/hotels/${hotel.slug}`}
      className={cn(
        'group flex flex-col rounded-2xl border border-hms-border bg-hms-card',
        'overflow-hidden transition-all duration-300',
        'hover:scale-[1.02] hover:border-hms-gold/20 hover:shadow-lg hover:shadow-hms-gold/5',
      )}
    >
      {/* Image */}
      <div className="relative h-48 w-full overflow-hidden">
        <img
          src={hotel.image}
          alt={hotel.name}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        <div className="absolute top-3 right-3 rounded-full bg-hms-bg/70 backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold text-hms-gold border border-hms-gold/20">
          {hotel.stars}{t('hotelCard.star')}
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-5 space-y-3">
        <div>
          <h3 className="text-base font-semibold text-hms-text tracking-tight group-hover:text-hms-gold transition-colors">
            {hotel.name}
          </h3>
          <div className="flex items-center gap-1.5 mt-1.5">
            <MapPin size={13} className="text-hms-gold shrink-0" />
            <span className="text-xs text-hms-textSub truncate">{hotel.location}</span>
          </div>
        </div>

        <StarRating count={hotel.stars} />

        {/* Amenity badges */}
        <div className="flex flex-wrap gap-1.5">
          {hotel.amenities.map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 rounded-full border border-hms-border bg-hms-surface px-2.5 py-1 text-[10px] font-medium text-hms-textSub"
            >
              <Icon size={11} className="text-hms-teal" />
              {label}
            </span>
          ))}
        </div>

        {/* Price + CTA */}
        <div className="mt-auto flex items-end justify-between pt-3 border-t border-hms-border/50">
          <div>
            <span className="text-lg font-bold text-hms-gold">
              {formatMoney(hotel.price)}
            </span>
            <span className="ml-1.5 text-xs text-hms-textDim line-through">
              {formatMoney(hotel.oldPrice)}
            </span>
            <p className="text-[10px] text-hms-textDim mt-0.5">{t('featuredHotels.perNight')}</p>
          </div>
          <span
            id={`hotels-featured-view-${index}`}
            className={cn(
              'inline-flex items-center gap-1 rounded-lg px-3 py-1.5',
              'text-[11px] font-semibold text-hms-gold',
              'border border-hms-gold/20 bg-hms-gold/5',
              'group-hover:bg-hms-gold group-hover:text-black transition-all',
            )}
          >
            {t('featuredHotels.viewDetails')}
            <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}

/* -------------------------------------------------------------------------- */
/*  FeaturedHotels                                                             */
/* -------------------------------------------------------------------------- */

export function FeaturedHotels() {
  const { t } = useTranslation();
  const { data: allHotels, isLoading } = usePublicHotels();
  const featuredHotels: FeaturedHotelCard[] = (allHotels ?? []).slice(0, 4).map((h) => ({
    slug: h.id,
    name: h.name,
    location: h.location,
    latitude: h.latitude,
    longitude: h.longitude,
    stars: h.stars,
    price: h.price,
    // No price means no "was" price either.
    //
    // `null * 1.2` is 0, so an unpriced hotel rendered as Rs 0 slashed from
    // Rs 0 — inventing a discount on a rate that does not exist.
    oldPrice: typeof h.price === 'number' ? Math.round(h.price * 1.2) : null,
    image: h.image,
    amenities: [],
    isFeatured: true,
  }));

  return (
    <section id="hotels-featured-section" className="relative w-full bg-hms-surface/30 py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-hms-text tracking-tight sm:text-4xl">
            {t('featuredHotels.title')}
          </h2>
          <div className="mx-auto mt-3 h-0.5 w-16 rounded-full bg-hms-gold" />
          <p className="mx-auto mt-4 max-w-md text-sm text-hms-textSub leading-relaxed">
            {t('featuredHotels.subtitle')}
          </p>
        </div>

        {/* Grid */}
        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
          ) : (
            featuredHotels.map((hotel, index) => (
              <HotelCardItem key={hotel.slug} hotel={hotel} index={index} />
            ))
          )}
        </div>

        {/* CTA */}
        <div className="mt-12 flex justify-center">
          <Link
            id="hotels-featured-view-all-btn"
            href="/hotels"
            className={cn(
              'inline-flex items-center gap-2 rounded-xl px-8 py-3',
              'bg-hms-gold text-sm font-semibold text-black',
              'shadow-lg shadow-hms-gold/20 transition-all',
              'hover:brightness-110 hover:shadow-hms-gold/30 active:scale-[0.98]',
            )}
          >
            {t('featuredHotels.viewAllHotels')}
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
